import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Loader2, LogOut, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { getOwnerToken, setOwnerToken, getOwnerEmail } from "@/lib/session";
import { fetchTiers } from "@/lib/sponsor";

import OwnerLogin from "@/components/owner/OwnerLogin";
import SummaryStats from "@/components/owner/SummaryStats";
import ClaimCard from "@/components/owner/ClaimCard";
import SponsorshipCard from "@/components/owner/SponsorshipCard";
import MarketingPlansSection from "@/components/owner/MarketingPlansSection";
import ContactAdminCard from "@/components/owner/ContactAdminCard";

/**
 * OwnerPage — top-level shell.
 *
 * Responsibilities (everything else lives in /components/owner/):
 *   - Hold the owner token (localStorage-backed) and route between the
 *     login screen and the authenticated dashboard.
 *   - Handle magic-link auto-login when the URL carries `?token=<magic>`
 *     (sent in claim-approval emails). The magic token is exchanged once
 *     for the canonical 14-day owner session, then scrubbed from the URL.
 *   - Fetch /owner/me + tiers and pass them down to the section
 *     components. Sponsorship extend/cancel APIs are also wired here
 *     because they need a refresh of /owner/me after a successful action.
 */
export default function OwnerPage() {
  const [token, setToken] = useState(() => getOwnerToken());

  const handleAuthed = (t, email) => {
    setOwnerToken(t, email);
    setToken(t);
  };

  const handleLogout = () => {
    setOwnerToken(null);
    setToken(null);
  };

  // Magic-link auto-login: when the URL has `?token=<magic>` from a claim
  // approval email, validate it server-side once and persist as the canonical
  // owner session.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const magic = params.get("token");
    if (!magic || token) return;
    (async () => {
      try {
        const res = await api.post("/owner/session_from_url", { token: magic });
        toast.success("Signed in — welcome back");
        handleAuthed(res.data.owner_token, res.data.email);
        // Scrub the token from the URL so it can't be re-used by sharing.
        const url = new URL(window.location.href);
        url.searchParams.delete("token");
        window.history.replaceState({}, "", url.toString());
      } catch (err) {
        toast.error("Sign-in link expired", {
          description:
            err?.response?.data?.detail ||
            "Please log in with your email instead.",
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!token) return <OwnerLogin onAuthed={handleAuthed} />;
  return <OwnerDashboard token={token} onLogout={handleLogout} />;
}

// ---------- Authenticated dashboard ----------
function OwnerDashboard({ token, onLogout }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([]);
  const [extending, setExtending] = useState(null); // `${claim_id}:${tier_id}`
  const [cancelling, setCancelling] = useState(null); // claim_id

  const load = async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get("/owner/me", { headers: { "X-Owner-Token": token } }),
        fetchTiers(),
      ]);
      setMe(m.data);
      setTiers(t);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleExtend = async (claimId, tierId) => {
    setExtending(claimId + ":" + tierId);
    try {
      const origin = window.location.origin;
      const res = await api.post(
        "/owner/extend",
        { claim_id: claimId, tier_id: tierId, origin_url: origin },
        { headers: { "X-Owner-Token": token } }
      );
      try {
        sessionStorage.setItem(
          "halal_sponsor_session",
          JSON.stringify({
            session_id: res.data.session_id,
            tier_id: tierId,
          })
        );
      } catch (storageErr) {
        console.warn(
          "[owner-extend] sessionStorage write failed:",
          storageErr?.message
        );
      }
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Couldn't start checkout", {
        description: err?.response?.data?.detail || err.message,
      });
      setExtending(null);
    }
  };

  const handleCancel = async (claimId, restaurantName) => {
    if (!claimId) {
      toast.error("Couldn't cancel — claim id missing");
      return;
    }
    const ok = window.confirm(
      `End featured status for "${restaurantName || "this listing"}" now?\n\n` +
        "Your listing will stop appearing as Featured immediately. " +
        "Past payments aren't refunded."
    );
    if (!ok) return;
    setCancelling(claimId);
    try {
      await api.post(
        "/owner/cancel",
        { claim_id: claimId },
        { headers: { "X-Owner-Token": token } }
      );
      toast.success("Sponsorship cancelled");
      // Refresh dashboard
      const fresh = await api.get("/owner/me", {
        headers: { "X-Owner-Token": token },
      });
      setMe(fresh.data);
    } catch (err) {
      toast.error("Cancel failed", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div
      data-testid="owner-dashboard"
      className="min-h-screen w-full bg-[#FDFBF7]"
    >
      <header className="border-b border-[#E5E1D8] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[#647466] hover:text-[#2A3B2C]">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="w-9 h-9 rounded-xl bg-[#D95D39] flex items-center justify-center">
              <Crown className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-display text-lg font-medium text-[#2A3B2C]">
                Your listings
              </h1>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8A9A8B]">
                {getOwnerEmail() || "Owner dashboard"}
              </p>
            </div>
          </div>
          <button
            data-testid="owner-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-1.5 text-xs text-[#647466] hover:text-[#D95D39]"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-5 h-5 animate-spin text-[#D95D39]" />
          </div>
        ) : !me ? (
          <p className="text-center text-[#647466]">No data</p>
        ) : (
          <>
            <SummaryStats me={me} />

            {/* Verified claims (each gets edit panel + badge purchase CTA) */}
            {me.claims && me.claims.length > 0 && (
              <div className="mt-6 space-y-3" data-testid="owner-claims">
                <h2 className="font-display text-lg font-medium text-[#2A3B2C]">
                  Your listings
                </h2>
                {me.claims.map((c) => (
                  <ClaimCard
                    key={c.claim_id}
                    claim={c}
                    token={token}
                    onChanged={load}
                  />
                ))}
              </div>
            )}

            {/* Always-visible marketing plans (drives conversions) */}
            <MarketingPlansSection
              tiers={tiers}
              claims={me.claims || []}
              token={token}
            />

            {/* Sponsorship history */}
            {me.sponsorships.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-dashed border-[#E5E1D8] bg-white p-8 text-center">
                <ShieldCheck className="w-7 h-7 mx-auto text-[#8A9A8B]" />
                <p className="mt-2 font-display text-lg text-[#2A3B2C]">
                  No sponsorships yet
                </p>
                <p className="text-sm text-[#647466] mt-1">
                  Find your restaurant on the map and tap &quot;Own this?&quot;
                  to claim and boost it.
                </p>
                <Link
                  to="/"
                  className="mt-4 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[#D95D39] hover:bg-[#C54E2C] text-white text-sm font-medium"
                >
                  Find my restaurant
                </Link>
              </div>
            ) : (
              <div
                className="mt-6 space-y-3"
                data-testid="owner-sponsorships"
              >
                <h2 className="font-display text-lg font-medium text-[#2A3B2C]">
                  Active sponsorships
                </h2>
                {me.sponsorships.map((s) => (
                  <SponsorshipCard
                    key={s.place_id + s.featured_until}
                    s={s}
                    tiers={tiers}
                    onExtend={(tierId) => handleExtend(s.claim_id, tierId)}
                    extending={extending}
                    onCancel={() =>
                      handleCancel(s.claim_id, s.restaurant_name)
                    }
                    cancelling={cancelling === s.claim_id}
                  />
                ))}
              </div>
            )}

            {/* Contact admin — always available for queries */}
            <ContactAdminCard token={token} />
          </>
        )}
      </main>
    </div>
  );
}
